/* eslint-disable no-console */
/* eslint-disable max-len */
import React, { useContext, useState } from 'react';
import { Box, LinearProgress, Stack, TextField, Tooltip, Typography } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { MdOutlineEdit } from 'react-icons/md';
import { LanguageContext } from '../../../../libs/Context';
import AddPoll from '../LeftPart/FeedCreation/AddPoll';
import { submitPoll } from '../../../actions/feeds_actions_new';
import { catchErr } from '../../../../libs/HelperContent';
import Loading from '../../../../components/Common/Loaders/FullWidthLoader/Loading';
import { FaCheckCircle } from 'react-icons/fa';
import { useSelector } from 'react-redux';

const SamplePoll = (props) =>
{
	const { language } = useContext(LanguageContext);
	const {
		bordercolor,
		pollCard,
		handlePollData,
		from,
		handleUpdateFeed,
		feed,
		noAction,
		requestT,
		type,
		selectedActivity
	} = props;

	const [ editPolls, setEditPolls ] = useState(false);
	const [ loading, setLoading ] = useState(false);
	const pollOptions = from === 'list' ? feed?.pollData?.options : pollCard?.options;
	const userData = useSelector((state:any) => state?.mysession?.session);

	const durationLeft = {
		1  : `1 ${language?.feedDay.toLowerCase()}`,
		2  : `2 ${language?.day.toLowerCase()}s`,
		7  : `1 ${language?.week.toLowerCase()}`,
		14 : `2 ${language?.week.toLowerCase()}s`
	};

	// Check if the poll has expired
	const pollStartT = feed?.postedAt;
	const pollEndT = pollStartT + (feed?.pollData?.inlynkDurationInDays * 24 * 60 * 60 * 1000);
	const currentTime = new Date().valueOf();
	const isPollExpired = currentTime > pollEndT;
	const isPollSubmitted = feed?.pollsubmitted;

	const getBackgroundStyle = (poll) =>
	{
		const totalSubmitted = feed?.pollData?.totalSubmittedCount || 0;
		const percentage = totalSubmitted > 0
			? (poll?.submitCount / totalSubmitted) * 100
			: 0;

		const baseStyles = {
			border       : `1px solid ${bordercolor}`,
			borderRadius : '20px',
			overflow     : 'hidden',
			position     : 'relative'
		};

		// Case 1: Poll not expired and not submitted
		if (!isPollExpired && !isPollSubmitted)
		{
			return {
				...baseStyles,
				backgroundColor : 'transparent'
			};
		}

		// Case 2: Poll expired and not submitted
		if (isPollExpired && !isPollSubmitted && feed?.createdBy !== userData?.userId)
		{
			return {
				...baseStyles,
				backgroundColor : 'transparent'
			};
		}

		// Case 3: Poll expired or submitted with results
		return {
			...baseStyles,
			'&::before' : {
				content         : '""',
				position        : 'absolute',
				top             : 0,
				left            : 0,
				right           : 0,
				bottom          : 0,
				backgroundColor : poll?.submitCount > 0 ? 'rgba(36, 164, 196, 0.4)' : '',
				width           : `${percentage}%`,
				borderRadius    : '10px 0px 0px 10px'
			},
			backgroundColor : poll?.submitCount === 0 ? '' : 'transparent'
		};
	};

	const handleSubmitPoll = async (option) =>
	{
		if (noAction || isPollExpired || type === 'draft') return;

		setLoading(true);
		const body = {
			feedId : feed?._id,
			option : [ option ]
		};

		try
		{
			const res = await submitPoll(body);

			if (res?.status)
			{
				const obj = {
					pollsubmitted   : true,
					pollData        : res?.data?.pollData,
					submittedOption : res?.data?.submittedOption
				};

				handleUpdateFeed(obj);
			}
		}
		catch (err)
		{
			console.error('Poll submission error:', err);
			catchErr(err);
		}
		finally
		{
			setLoading(false);
		}
	};

	// Get the remaining time in days, hours, and minutes
	const getDuration = (days) =>
	{
		/**
		As per the posted feeds, if user is not scheduling the poll
		then the poll will be expired as per the postedAt time,
		otherwise it will be expired as per the scheduledAt time.
		*/

		let startTime;

		if (feed?.isActive)
		{
			startTime = selectedActivity === 'schedule' ? feed?.scheduledAt : feed?.postedAt;
		}
		else
		{
			startTime = Date.now();
		}
		// const startTime = selectedActivity === 'schedule' ? feed?.scheduledAt : feed?.postedAt;
		const endTime = startTime + (days * 24 * 60 * 60 * 1000);
		const remainingTime = endTime - new Date().valueOf();

		const remainingDays = Math.floor(remainingTime / (24 * 60 * 60 * 1000));
		const remainingHours = Math.floor((remainingTime % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
		const remainingMinutes = Math.floor((remainingTime % (60 * 60 * 1000)) / (60 * 1000));

		if (remainingTime <= 0) return language?.pollClosed;
		// if (remainingDays >= 14) return '2 weeks left';
		// if (remainingDays >= 7) return '1 week left';
		if (remainingDays > 1) return `${remainingDays} ${language?.daysLeft}`;
		if (remainingDays === 1) return `${language?.oneDayLeft}`;
		if (remainingHours > 1) return `${remainingHours} ${language?.hrsLeft}`;
		if (remainingHours === 1) return `${language?.oneHr}`;
		if (remainingMinutes > 1) return `${remainingMinutes} ${language?.minsLeft}`;
		if (remainingMinutes === 1) return `${language?.oneMnt}`;

		return language?.pollClosed;
	};

	// Update the renderPollOptions function to use the new background style
	const renderPollOptions = () =>
	{
		if (from === 'list')
		{
			return (
				isPollExpired
					? (
						<Stack spacing={1.5}>
							{pollOptions?.map((poll, index) =>
							{
								const percentage = feed?.pollData?.totalSubmittedCount > 0
									? parseFloat(((poll?.submitCount / feed?.pollData?.totalSubmittedCount) * 100).toFixed(2))
									: 0;

								return (
									<Box key={`${poll?.option}_${index}`} sx={{ width: '100%' }}>
										<Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
											{/* Show Check Icon for Selected Option */}
											{feed?.submittedOption === poll?.option &&
												<FaCheckCircle fontSize={18} color='rgb(57 195 59)' style={{ marginLeft: 0, marginRight: 10 }} />
											}
											<Typography fontSize={14} sx={{ textTransform: 'capitalize' }}>
												{poll?.option}
											</Typography>
										</Box>
										<Stack direction='row' spacing={1} width='100%' alignItems='center'>
											<Box width='90%'>
												<LinearProgress
													variant='determinate'
													value={percentage}
													sx={{
														height                     : 8,
														borderRadius               : 5,
														backgroundColor            : '#e0e0e0',
														'& .MuiLinearProgress-bar' : { backgroundColor: '#17a2b8' }
													}}
												/>
											</Box>
											<Typography fontWeight='var(--font500)' fontSize={14}>
												{percentage}%
											</Typography>
										</Stack>

									</Box>
								);
							})}
						</Stack>
					)
					: (
						<Stack spacing={2}>
							{pollOptions?.map((poll, index) => (
								<Box
									key={`${poll?.option}_${index}`}
									sx={{
										width      : '100%',
										height     : 'auto',
										display    : 'flex',
										alignItems : 'center',
										px         : 2,
										py         : 1,
										...getBackgroundStyle(poll),
										cursor     : !isPollExpired && type !== 'draft' ? 'pointer' : 'default'
									}}
									onClick={() =>
									{
										if (type !== 'draft' || !isPollExpired)
										{
											handleSubmitPoll(poll?.option);
										}
									}}
									// onClick={() => { type !== 'draft' || !isPollExpired && handleSubmitPoll(poll?.option); }}
								>
									<Box
										sx={{
											display       : 'flex',
											flexDirection : 'row',
											alignItems    : 'center',
											width         : '100%',
											position      : 'relative',
											zIndex        : 1
										}}
									>
										<Typography
											fontWeight='var(--font500)'
											fontSize={14}
											color={'var(--black)'}
											sx={{ textTransform: 'capitalize', mr: 1 }}
										>
											{poll?.option}
										</Typography>
										<Typography
											fontWeight='var(--font500)'
											fontSize={14}
											color={'var(--black)'}
										>
											{feed?.pollData?.totalSubmittedCount > 0
												? parseFloat(Number(((poll?.submitCount / feed?.pollData?.totalSubmittedCount) * 100)).toFixed(2))
												: '0'}
											%
										</Typography>
										<Box sx={{ flexGrow: 1 }} />
										{feed?.submittedOption === poll?.option && (
											<FaCheckCircle fontSize={20} color={poll?.submitCount > 0 ? 'rgb(57 195 59)' : undefined} />
										)}
									</Box>
								</Box>
							))}
						</Stack>
					)
			);
		}

		if (from === 'list' && !isPollExpired && selectedActivity !== 'schedule')
		{
			return (
				<Stack spacing={1}>
					{pollOptions?.map((value, index) => (
						<TextField
							key={`${value?.option}_list${index}`}
							defaultValue={value?.option ?? ''}
							fullWidth
							inputProps={{ maxLength: 25 }}
							size='small'
							variant='outlined'
							InputProps={{
								readOnly : true,
								sx       : { cursor: type !== 'draft' ? 'pointer' : 'default' }
							}}
							sx={{
								borderRadius               : 'var(--pixel28)',
								'& .MuiOutlinedInput-root' : {
									borderRadius : 'var(--pixel28)',
									'& fieldset' : {
										borderColor : bordercolor
									},
									'&:hover fieldset' : {
										borderColor   : bordercolor,
										pointerEvents : type === 'draft' ? 'none' : 'auto'
									},
									'&.Mui-focused fieldset' : {
										borderColor : bordercolor
									}
								}
							}}
							onClick={() => type !== 'draft' && handleSubmitPoll(value?.option)}
						/>
					))}
				</Stack>
			);
		}

		return (
			<Stack spacing={1}>
				{pollOptions?.map((value, index) => (
					<Box
						key={`${value?.option}_create${index}`}
						sx={{
							width        : '100%',
							padding      : 'var(--pixel8)',
							borderRadius : 'var(--pixel28)',
							border       : `1px solid ${bordercolor}`,
							wordBreak    : 'break-all'
						}}
					>
						{value?.option ?? ''}
					</Box>
				))}
			</Stack>
		);
	};

	return (
		<Box sx={{
			height       : 'var(--percent100)',
			width        : 'var(--percent100)',
			border       : '1px solid #949494',
			borderRadius : '13px',
			p            : 2
		}} mt={1}
		>
			{loading && <Loading />}

			<Stack direction='row' justifyContent='space-between' alignItems='center' pb='var(--pixel16)'>
				<Typography variant='inherit' fontWeight={500}>
					{pollCard?.question ?? feed?.pollData?.question ?? ''}
				</Typography>

				{from === 'create' && (
					<Stack direction='row' spacing={1}>
						<Tooltip title={language.edit} placement='top'>
							<Box onClick={() => setEditPolls(true)}>
								<MdOutlineEdit style={{ cursor: 'pointer' }} fontSize='var(--pixel18)' />
							</Box>
						</Tooltip>
						{requestT !== 'editFeeds' && (
							<Tooltip title={language.close} placement='top'>
								<Box onClick={() => handlePollData({})}>
									<IoClose style={{ cursor: 'pointer' }} fontSize='var(--pixel18)' />
								</Box>
							</Tooltip>
						)}
					</Stack>
				)}
			</Stack>

			{renderPollOptions()}
			{/* Poll Duration Section */}
			{ type !== 'draft' &&
			<Typography
				variant='body1'
				sx={{
					pt    : 1,
					color : isPollExpired ? '#FFA500' : 'var(--black)'
				}}
			>
				{from === 'list'
					? getDuration(pollCard?.inlynkDurationInDays)
					: `${durationLeft?.[pollCard?.inlynkDurationInDays]} ${language?.left?.toLowerCase()}`
				}

			</Typography>}

			{editPolls && (
				<AddPoll
					pollOpen={editPolls}
					handlepollClose={() => setEditPolls(false)}
					editPoll={pollCard}
					from='edit'
					handlePollData={handlePollData}
				/>
			)}
		</Box>
	);
};

export default SamplePoll;